'use strict';

var React = require('react');
var genericForwardRef = require('../../../helpers/genericForwardRef.js');
var keyboard = require('../../../helpers/keyboard.js');
require('../../../theme/breakpoints.js');
var useClickAway = require('../../../hooks/useClickAway.js');
require('imask');
var useMaybeControlledValue = require('../../../hooks/useMaybeControlledValue.js');
var useRefAndForward = require('../../../hooks/useRefAndForward.js');
require('resize-observer-polyfill');
var useValidityState = require('../../../hooks/useValidityState.js');
var component = require('../../Icon/component.js');
require('../../Tooltip/component.js');
var component$2 = require('../../Overlay/component.js');
var component$1 = require('../Control/component.js');
var context = require('./context.js');
var styles = require('./styles.js');
var useSearch = require('./useSearch.js');

function _interopDefaultCompat (e) { return e && typeof e === 'object' && 'default' in e ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefaultCompat(React);

const defaultSuffix = React__default.default.createElement(component.Icon, { name: "down-filled", size: "m" });
const SelectControlImpl = ({ controlRef = null, radio, onChange, value, search, isOpened: maybeIsOpened, onOpenedChange: maybeOnOpenedChange, optionsWrapperId, onSearchChange, formatLabel = String, overlayMaxHeight, children, placeholder, required = false, suffix = defaultSuffix, onValidityChange, onFocus, disableKeyboardArrowSelection, disablePortalForOptions, ...controlProps }, ref) => {
    const [isOpened, onOpenedChange] = useMaybeControlledValue.useMaybeControlledValue({
        valueFromProps: maybeIsOpened,
        onChangeFromProps: maybeOnOpenedChange,
        defaultValue: false,
    });
    const isSearchable = !!onSearchChange && search !== undefined;
    const overlayRef = useRefAndForward.useRefAndForward(null, ref);
    const controlLocalRef = useRefAndForward.useRefAndForward(null, controlRef);
    const context$1 = React.useMemo(() => ({
        formatLabel: (optionValue) => (optionValue ? formatLabel(optionValue) : ''),
        onChange: (nextValue) => {
            onOpenedChange(false);
            onChange(nextValue);
        },
        value,
        variant: radio ? 'radio' : 'select',
        values: null,
    }), [radio, onChange, onOpenedChange, value, formatLabel]);
    const labelFromValue = context$1.formatLabel(value);
    const input = useSearch.useSearch({ labelFromValue, onSearchChange, search, placeholder, isOpened });
    useClickAway.useClickAway({
        containerRefs: [controlLocalRef, overlayRef],
        listener: React.useCallback(() => {
            if (isOpened)
                onOpenedChange(false);
        }, [isOpened, onOpenedChange]),
    });
    useValidityState.useValidityState({
        onValidityChange,
        getState: React.useCallback(() => ({ valueMissing: required && value === null }), [required, value]),
    });
    const commonControlProps = {
        ref: controlLocalRef,
        role: 'listbox',
        suffix,
        onFocus: (e) => {
            if (controlProps.disabled)
                return;
            onOpenedChange(true);
            if (onFocus) {
                onFocus(e);
            }
        },
        style: {
            pointerEvents: !controlProps.disabled ? 'auto' : 'none',
            cursor: !controlProps.disabled ? 'pointer' : 'not-allowed',
            minHeight: '2.625rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
        'aria-multiselectable': false,
        'aria-required': required,
    };
    const getChildrenElements = React.useCallback(() => {
        const childs = children;
        const child = childs.filter(item => (item?.key ? childs : item?.[0] ? item[0] : null));
        if (!child || !child.length) {
            return;
        }
        return child[0][0] ? child[0] : child;
    }, [children]);
    const selectOption = React.useCallback((direction) => {
        const child = getChildrenElements();
        if (!child) {
            return;
        }
        let selectedKey = child.findIndex((item) => formatLabel(item?.props?.value) === formatLabel(value));
        if (direction === 'up') {
            selectedKey = selectedKey - 1;
        }
        if (direction === 'bottom') {
            selectedKey = selectedKey + 1;
        }
        const nextValue = child[selectedKey]?.props?.value;
        if (nextValue) {
            onChange(nextValue);
        }
    }, [formatLabel, getChildrenElements, onChange, value]);
    const selectValue = React.useCallback((event) => {
        onOpenedChange(false);
        const target = event.target;
        target.blur();
    }, [onOpenedChange]);
    const handleKeyDown = React.useCallback((event) => {
        if (keyboard.isKey('enter')(event)) {
            selectValue(event);
        }
        if (disableKeyboardArrowSelection) {
            return;
        }
        if (keyboard.isKey('up')(event)) {
            selectOption('up');
        }
        if (keyboard.isKey('bottom')(event)) {
            selectOption('bottom');
        }
    }, [disableKeyboardArrowSelection, selectOption, selectValue]);
    return (React__default.default.createElement(context.SelectProvider, { value: context$1 },
        isSearchable ? (React__default.default.createElement(component$1.Control, { as: "input", placeholder: input.placeholder, value: input.value, onChange: (event) => onSearchChange?.(event.target.value), onKeyDown: handleKeyDown, autoComplete: "off", ...controlProps, ...commonControlProps })) : (React__default.default.createElement(component$1.Control, { onKeyDown: handleKeyDown, ...controlProps, ...commonControlProps, tabIndex: 0 },
            value && context$1.formatLabel(value),
            !value && placeholder && React__default.default.createElement(styles.Styled.Placeholder, null, placeholder))),
        React__default.default.createElement(component$2.Overlay, { variant: "fit-target", ref: overlayRef, targetRef: controlLocalRef, isOpened: isOpened, maxHeight: overlayMaxHeight, disablePortalForOptions: disablePortalForOptions, id: optionsWrapperId }, children)));
};
const SelectControl = genericForwardRef.genericForwardRef(SelectControlImpl);
SelectControl.displayName = 'Select.Control';

exports.SelectControl = SelectControl;
//# sourceMappingURL=Control.js.map
